import { useMemo } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@emotion/react';
import { Grid, Typography } from '@mui/material';

// Our Assets
import dollarIcon from 'assets/icons/carousel_icons/dollarIcon.svg';
import loanInquiryIcon from 'assets/icons/carousel_icons/request_loan_icon.svg';
import totalLoansIcon from 'assets/icons/carousel_icons/totalLoansIcon.svg';

// Tooltip Data
import highlightCarouselTooltips from 'components/FADashboard/HighlightCarousel/highlightCarouselTooltips.json';

// Our Components
import HighlightCard from 'components/FADashboard/HighlightCarousel/HightlightCard';

// Our Routes
import { LOAN_INQUIRY_ROUTE } from 'routes';

// Our Utils
import { numbroAveragedToDecimal } from 'shared/utils';

function HighlightCards({ totalLoansUnderManagement }) {
	const SoraTheme = useTheme();
	const { carouselGradient, primary } = SoraTheme.palette;

	const navigate = useNavigate();

	const formattedTotalLoansUnderManagement = useMemo(
		() => numbroAveragedToDecimal(totalLoansUnderManagement ?? 0),
		[totalLoansUnderManagement]
	);

	const hightlightCards = useMemo(() => {
		const cards = [
			{
				background: carouselGradient.gradient1,
				buttonText: 'Request now',
				cardHeading: 'Price a Loan',
				cardContentValue: (
					<Typography variant="body2" sx={{ color: primary.indigo }}>
						Get offers on new <br />
						consumer or
						<br />
						commercial loans
					</Typography>
				),
				image: loanInquiryIcon,
				headingColor: SoraTheme.palette.primary.indigo,
				withToolTip: false,
				onClick: () => navigate(LOAN_INQUIRY_ROUTE)
			},
			{
				background: carouselGradient.gradient2,
				cardHeading: 'Debt Tolerance Assessment',
				cardContentVariant: 'body2',
				cardContentValue: (
					<Typography variant="body2" sx={{ color: primary.white }}>
						Assess your client&apos;s
						<br />
						comfort with debt.
						<br />
						Select a client to start.
					</Typography>
				),
				image: dollarIcon,
				headingColor: primary.white,
				type: 'debtTolereanceAssessment'
			},
			{
				background: carouselGradient.gradient3,
				cardHeading: (
					<Typography variant="h6" sx={{ color: primary.indigo }}>
						Total Liabilities Under
						<br /> Management
					</Typography>
				),
				cardContentValue: `$${formattedTotalLoansUnderManagement}`,
				image: totalLoansIcon,
				headingColor: primary.indigo,
				type: 'totalLiabilitiesUnderManagement'
			}
		];

		return cards.map(
			({
				type,
				background,
				buttonText,
				cardHeading,
				cardHeadingVariant,
				cardContentValue,
				cardContentVariant,
				headingColor,
				image,
				imageSX,
				withToolTip,
				onClick
			}) => {
				const uniqueIdentifier = uuidv4();
				const tooltipText = highlightCarouselTooltips[type];
				return (
					<Grid item xs={4} key={uniqueIdentifier}>
						<HighlightCard
							background={background}
							buttonText={buttonText}
							cardHeading={cardHeading}
							cardHeadingVariant={cardHeadingVariant}
							cardContentValue={cardContentValue}
							cardContentVariant={cardContentVariant}
							headingColor={headingColor}
							image={image}
							imageSX={imageSX}
							tooltipText={tooltipText}
							withToolTip={withToolTip ?? true}
							onClick={onClick}
						/>
					</Grid>
				);
			}
		);
	}, [formattedTotalLoansUnderManagement]);

	return hightlightCards;
}

export default HighlightCards;
